// Dependencies.
import React, { useState } from 'react'
import styled, { css } from 'styled-components'
import ScrollLock from 'react-scrolllock'

import { Section as SECTION } from '../../Section'
import { TimeField } from './TimeField'
import { HostedBy } from './HostedBy'
import { EventHookInterface } from '../../../types'
import { CoverImage } from './CoverImage'
import { TitleField } from './TitleField'
import { BookAndShare } from './BookAndShare'
import { forScreensGreaterThanMobileWidth } from '../../../utils/styles'
import { CoverImageModal } from './CoverImageModal'
import { TicketPrice } from './TicketPrice'
import { EventImage } from './EventImage'
import { EEventTypeValue } from '../../../entities'
import { RecurringEventBadge } from '../RecurringEventBadge'
import { HeaderDateTime as HeaderDateTimeBase } from './HeaderDateTime'
import { Content as RepeatEventSummaryContent } from '../RepeatEventSummary'
import { colors } from '../../../utils'

// Section.
const Section = styled(SECTION)`
  padding: 40px 0 20px;
  position: relative;

  &::before {
    background: #fafafe;
    content: '';
    height: calc(100% + 88px);
    left: 0;
    position: absolute;
    pointer-events: none;
    top: -48px;
    width: 100%;
    z-index: -1;
  }

  .details {
    box-sizing: border-box;
    display: flex;
    align-items: center;
    position: relative;
    flex-direction: column;
    row-gap: 40px;
  }
  .detailsContent {
    width: 100%;
  }
  .detailsHeader {
    display: flex;
    align-items: center;
    column-gap: 20px;
    width: 100%;
    align-items: flex-start;
  }
  .repeatEventSummaryContainer {
    color: ${colors.purple};
  }

  .titleField {
    margin-bottom: 14px;

    h1 {
      color: #a6a6a6;
    }
  }

  /* Desktop. */
  ${forScreensGreaterThanMobileWidth(css`
    .content {
      box-sizing: border-box;
      display: flex;
      justify-content: center;
    }

    &::before {
      height: 100%;
      left: calc(50% - 188px);
      top: 0;
      width: calc(50% + 188px);
    }

    .details {
      box-sizing: border-box;
      padding-left: 60px;
      width: 50%;
      max-width: 447px;
    }

    &.readOnly .details {
    }
  `)}
  .hostedByContainer {
    margin-top: 22px;
  }
`
const HeaderDateTime = styled(HeaderDateTimeBase)`
  margin-bottom: 8px;
`

// Props.
interface Props {
  eventHook: EventHookInterface
  hostGroupId?: number
  handleNotGoing?: () => void
  className?: string
  setGroupId?: (id: number) => void
}

// Hero.
export const Hero: React.FC<Props> = (props) => {
  const [showModal, setShowModal] = useState(false)

  const {
    eventHook,
    hostGroupId,
    handleNotGoing,
    className = '',
    setGroupId,
  } = props
  const { isEditable, value } = eventHook
  const { is_recurring } = value
  return (
    <Section className={`${(!isEditable && 'readOnly') || ''} ${className}`}>
      {/* Cover Image. */}
      {!isEditable ? (
        <EventImage eventHook={eventHook} />
      ) : (
        <CoverImage
          eventHook={eventHook}
          showModal={() => setShowModal(true)}
        />
      )}

      {/* Cover Image Modal */}
      {showModal && isEditable && (
        <CoverImageModal
          eventHook={eventHook}
          hideModal={() => setShowModal(false)}
        />
      )}
      {showModal && isEditable && <ScrollLock isActive={showModal} />}
      <div className="details">
        {is_recurring && !isEditable && (
          <div className="detailsHeader">
            <RecurringEventBadge />
            <div className="repeatEventSummaryContainer">
              <RepeatEventSummaryContent event={value} showUntil={false} />
            </div>
          </div>
        )}
        <div className="detailsContent">
          {/* Date. */}
          {!isEditable && (
            <HeaderDateTime dateTime={eventHook.value.start_date} />
          )}

          {/* Title. */}
          <TitleField eventHook={eventHook} />

          {/* Time. */}
          <TimeField eventHook={eventHook} />

          {/* Host. */}
          <div className="hostedByContainer">
            <HostedBy
              eventHook={eventHook}
              hostGroupId={hostGroupId}
              setGroupId={setGroupId}
            />
          </div>

          {/* Price. */}
          {!isEditable &&
            eventHook.value.event_paid_type === EEventTypeValue.PAID && (
              <TicketPrice eventHook={eventHook} />
            )}

          {/* Book And Share. */}
          <BookAndShare eventHook={eventHook} handleNotGoing={handleNotGoing} />

          {/* Number of participants. */}
          {/* <NumberOfParticipants eventHook={eventHook} /> */}

          {/* Note. */}
          {/* <LinkNotice eventHook={eventHook} /> */}
        </div>
      </div>
    </Section>
  )
}
